import { useEffect, useRef, useState } from "react";
import { Globe, History, Radar, Search } from "lucide-react";
import { cn } from "@wa/ui";
import { BAND_DOT, scoreBand, type ScanListItem } from "../../../lib/siteScan";
import { Btn } from "../../ui";
import { SCAN_STAGES, type ScanStage } from "./useSiteScan";
import type { T } from "./types";

/**
 * The scanner's entry point: one field, one button, and the last few sites the
 * team looked at.
 *
 * The field is the whole page when nothing has been scanned yet, which is the
 * point - there is exactly one thing to do here. Once a result exists it shrinks
 * to a bar above it, so scanning the next site is one keystroke away without the
 * result jumping around.
 */

function RecentChip({ item, onPick, t }: { item: ScanListItem; onPick: (domain: string) => void; t: T }) {
  const band = item.opportunityScore == null ? null : scoreBand(item.opportunityScore);
  return (
    <button
      type="button"
      onClick={() => onPick(item.domain)}
      title={item.cmsName ?? t("scanner.platform.unknown")}
      // The CRM's word-plus-mark toggle, used here as a shortcut rather than a
      // filter: same shape, same hover, one less private button style.
      className="crm-cb crm-cb-wide crm-cb-sm max-w-[15rem]"
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", band ? BAND_DOT[band] : "bg-muted-foreground/40")} />
      <span className="crm-trunc">{item.domain}</span>
      {item.cmsName && <span className="crm-trunc font-normal opacity-70">{item.cmsName}</span>}
    </button>
  );
}

/** The staged rail shown while a scan is in flight. */
function ScanProgress({ stage, target, t }: { stage: ScanStage; target: string; t: T }) {
  const index = SCAN_STAGES.indexOf(stage);
  return (
    <div className="crm-card mt-6 p-5">
      <div className="flex items-center gap-3">
        <span className="relative flex size-9 items-center justify-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
          <Radar className="size-5 animate-pulse text-primary" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{t("scanner.progress.title", { target })}</p>
          <p className="text-xs text-muted-foreground">{t(`scanner.stage.${stage}`)}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-1.5">
        {SCAN_STAGES.map((s, i) => (
          <span
            key={s}
            className={cn(
              "h-1 rounded-full transition-colors duration-500",
              i < index ? "bg-primary" : i === index ? "bg-primary/60 animate-pulse" : "bg-[var(--crm-sunk)]",
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function ScanForm({
  compact,
  busy,
  busyTarget,
  stage,
  recent,
  onScan,
  t,
}: {
  /** Compact mode is the bar above an existing result. */
  compact: boolean;
  busy: boolean;
  busyTarget: string;
  stage: ScanStage;
  recent: ScanListItem[];
  onScan: (url: string) => void;
  t: T;
}) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // The field is the only control on an empty page, so it takes focus.
  useEffect(() => {
    if (!compact) inputRef.current?.focus();
  }, [compact]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onScan(value);
  };

  const pick = (domain: string) => {
    setValue(domain);
    onScan(domain);
  };

  return (
    <section className={cn(compact ? "" : "pb-2 sm:pb-3")}>
      {!compact && (
        // Wide enough that the title holds one line on a laptop, and short
        // enough vertically that the scanned-sites table is not pushed under
        // the fold on the one screen an operator opens every morning. The page
        // head above already says what this screen is for, so this block is
        // down to the badge, the promise and the field.
        <div className="mx-auto mb-4 max-w-2xl text-center">
          <span className="crm-chip">
            <Radar className="size-3.5 text-primary" /> {t("scanner.badge")}
          </span>
          <h2 className="crm-h1 mt-2.5 text-balance text-[24px] sm:text-[30px]">{t("scanner.hero.title")}</h2>
          <p className="mx-auto mt-1.5 max-w-lg text-[13px] text-muted-foreground">{t("scanner.hero.subtitle")}</p>
        </div>
      )}

      <form onSubmit={submit} className={cn("mx-auto", compact ? "max-w-none" : "max-w-2xl")}>
        <div
          className={cn(
            "crm-card group flex items-center gap-2 p-1.5 transition-shadow",
            "focus-within:ring-4 focus-within:ring-primary/15",
          )}
        >
          <Globe className="ml-2.5 size-4 shrink-0 text-muted-foreground transition-colors group-focus-within:text-primary" />
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("scanner.form.placeholder")}
            spellCheck={false}
            autoComplete="off"
            inputMode="url"
            aria-label={t("scanner.form.placeholder")}
            className="min-w-0 flex-1 bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Btn type="submit" tone="brand" small={compact} disabled={busy || !value.trim()} className="shrink-0">
            <Search className={cn("size-4", busy && "animate-pulse")} />
            {t(busy ? "scanner.form.scanning" : "scanner.form.scan")}
          </Btn>
        </div>

        {busy && <ScanProgress stage={stage} target={busyTarget} t={t} />}

        {!busy && recent.length > 0 && (
          <div className={cn("flex items-center gap-2", compact ? "mt-3" : "mt-3.5")}>
            <History className="size-3.5 shrink-0 text-muted-foreground" />
            <div className="flex gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {recent.map((item) => (
                <RecentChip key={item.domain} item={item} onPick={pick} t={t} />
              ))}
            </div>
          </div>
        )}
      </form>
    </section>
  );
}
